import React, { useState, useEffect } from 'react';
import { Plus, X, RefreshCw, ChevronLeft, ChevronRight, Clock, Check, XCircle, Info } from 'lucide-react';
import { holidayRequestApi, blockedDateApi, employeeApi } from '../services/api';
import { HolidayRequest, CreateHolidayRequestRequest, BlockedDate, EmployeeLeaveTypeInfo } from '../types';
import { useAuth } from '../context/AuthContext';
import { formatCompanyDate, formatCompanyDateLocale } from '../utils/companyDateFormat';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, isSameMonth, isSameDay } from 'date-fns';

const MyHolidays: React.FC = () => {
  const { employee } = useAuth();
  const [userHolidays, setUserHolidays] = useState<HolidayRequest[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [statusPopup, setStatusPopup] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateHolidayRequestRequest>({
    employeeId: 0,
    managerSupervisor: '',
    venueDepartment: '',
    startDate: '',
    endDate: '',
    totalHours: undefined,
    reason: '',
    status: 'Pending',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [holidayTypes, setHolidayTypes] = useState<Record<string, string>>({});
  const [detailsPopupHoliday, setDetailsPopupHoliday] = useState<HolidayRequest | null>(null);
  const [employeeLeaveTypes, setEmployeeLeaveTypes] = useState<EmployeeLeaveTypeInfo[]>([]);

  const generateDateRange = (startDate: string, endDate: string): string[] => {
    if (!startDate || !endDate) return [];
    const dates: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const currentDate = new Date(start);
    while (currentDate <= end) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const dates = generateDateRange(formData.startDate, formData.endDate);
      setHolidayTypes(prev => {
        const newHolidayTypes: Record<string, string> = {};
        dates.forEach(date => { if (prev[date]) newHolidayTypes[date] = prev[date]; });
        return newHolidayTypes;
      });
    } else {
      setHolidayTypes({});
    }
  }, [formData.startDate, formData.endDate]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const [allHolidays, blocked, leaveTypes] = await Promise.all([
        holidayRequestApi.getAll(employee?.id),
        blockedDateApi.getAll(),
        employee?.id ? employeeApi.getLeaveTypes(employee.id, currentYear).catch(() => []) : Promise.resolve([]),
      ]);
      setUserHolidays(allHolidays || []);
      setBlockedDates(blocked || []);
      setEmployeeLeaveTypes(leaveTypes || []);
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadHolidays();
  }, [employee?.id]);

  const onRefresh = () => {
    setRefreshing(true);
    loadHolidays();
  };

  const isDateBlocked = (startDate: string, endDate: string): boolean => {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    return blockedDates.some(bd => {
      const bdStart = new Date(bd.startDate + 'T00:00:00');
      const bdEnd = new Date(bd.endDate + 'T00:00:00');
      return (
        (bdStart <= start && bdEnd >= start) ||
        (bdStart <= end && bdEnd >= end) ||
        (bdStart >= start && bdEnd <= end)
      );
    });
  };

  const handleAddHoliday = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee?.id) return;

    const errors: Record<string, string> = {};
    if (!formData.startDate) errors.startDate = 'Start date is required';
    if (!formData.endDate) errors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      errors.endDate = 'End date must be after or equal to start date';
    }
    if (!formData.managerSupervisor?.trim()) errors.managerSupervisor = 'Manager/Supervisor is required';
    if (!formData.venueDepartment?.trim()) errors.venueDepartment = 'Venue/Department is required';
    if (!formData.reason?.trim()) errors.reason = 'Reason is required';

    if (formData.startDate && formData.endDate && isDateBlocked(formData.startDate, formData.endDate)) {
      errors.startDate = 'Selected date range is blocked';
    }

    if (formData.startDate && formData.endDate) {
      const dates = generateDateRange(formData.startDate, formData.endDate);
      const missing = dates.filter(d => !holidayTypes[d] || !holidayTypes[d].trim());
      if (missing.length > 0) {
        errors.holidayTypes = 'Holiday type is required for all days';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      const holidayTypesArray = formData.startDate && formData.endDate
        ? generateDateRange(formData.startDate, formData.endDate)
            .sort()
            .map(date => ({ date, holidayType: holidayTypes[date] || '' }))
        : undefined;
      const totalHours =
        formData.totalHours != null && formData.totalHours > 0 ? formData.totalHours : undefined;
      await holidayRequestApi.create({
        ...formData,
        employeeId: employee.id,
        totalHours,
        holidayTypes: holidayTypesArray,
      });
      await loadHolidays();
      setShowAddModal(false);
      setFormData({
        employeeId: 0,
        managerSupervisor: '',
        venueDepartment: '',
        startDate: '',
        endDate: '',
        totalHours: undefined,
        reason: '',
        status: 'Pending',
      });
      setHolidayTypes({});
      setFormErrors({});
    } catch (error: any) {
      alert(error?.response?.data || error?.message || 'Failed to create holiday request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelHoliday = async (holiday: HolidayRequest) => {
    if (!window.confirm(`Are you sure you want to cancel your holiday request from ${holiday.startDate} to ${holiday.endDate}?`)) return;
    try {
      await holidayRequestApi.cancel(holiday);
      await loadHolidays();
    } catch (error: any) {
      alert(error?.response?.data || error?.message || 'Failed to cancel holiday');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'bg-green-50 text-green-700 border-green-200';
      case 'Pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Denied': return 'bg-red-50 text-red-700 border-red-200';
      case 'Cancelled': return 'bg-neutral-50 text-neutral-600 border-neutral-200';
      default: return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    const days = eachDayOfInterval({ start, end });
    const startDayOfWeek = start.getDay();
    const leadingEmpty = Array(startDayOfWeek).fill(null);
    return [...leadingEmpty, ...days];
  };

  const getHolidaysForDate = (date: Date | null): HolidayRequest[] => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return userHolidays.filter(hr => {
      const start = hr.startDate.split('T')[0];
      const end = hr.endDate.split('T')[0];
      return dateStr >= start && dateStr <= end;
    });
  };

  const getFilteredHolidaysForDate = (date: Date | null): HolidayRequest[] => getHolidaysForDate(date);

  const isDayBlocked = (date: Date | null): boolean => {
    if (!date) return false;
    return blockedDates.some(bd => {
      const bdStart = new Date(bd.startDate + 'T00:00:00');
      const bdEnd = new Date(bd.endDate + 'T00:00:00');
      return bdStart <= date && bdEnd >= date;
    });
  };

  const pendingHolidays = userHolidays.filter(h => h.status === 'Pending');
  const approvedHolidays = userHolidays.filter(h => h.status === 'Approved');
  const deniedHolidays = userHolidays.filter(h => h.status === 'Denied');
  const cancelledHolidays = userHolidays.filter(h => h.status === 'Cancelled');

  const pendingCount = pendingHolidays.length;
  const approvedCount = approvedHolidays.length;
  const deniedCount = deniedHolidays.length;
  const cancelledCount = cancelledHolidays.length;

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentMonth);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-neutral-800">My Holidays</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Holiday
          </button>
        </div>
      </div>

      {/* Leave Balances Summary */}
      {employeeLeaveTypes.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
          <h2 className="text-lg font-semibold text-neutral-800 mb-3">Your Leave Balances</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {employeeLeaveTypes.map(lt => (
              <div key={lt.leaveTypeId} className="flex flex-col gap-1 py-2 px-3 bg-neutral-50 rounded-lg border border-neutral-100">
                <span className="font-medium text-neutral-800">{lt.name}</span>
                <div className="text-sm flex flex-wrap gap-x-3 gap-y-0.5">
                  {lt.isAnnualLeave && lt.accruedDays != null && (
                    <span><span className="text-neutral-700 font-medium">{lt.accruedDays}</span> <span className="text-neutral-500">accrued</span></span>
                  )}
                  <span>
                    <span className="text-green-600 font-medium">{lt.takenDays}</span>{' '}
                    <span className="text-neutral-500">{lt.isAnnualLeave ? 'used' : 'approved'}</span>
                  </span>
                  <span><span className="text-amber-600 font-medium">{lt.pendingDays}</span> <span className="text-neutral-500">pending</span></span>
                  <span><span className="text-blue-600 font-medium">{lt.remainingDays}</span> <span className="text-neutral-500">available</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Panels (Clickable) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { status: 'Pending', count: pendingCount, holidays: pendingHolidays, icon: Clock, border: 'border-amber-200', bg: 'bg-amber-50', iconColor: 'text-amber-600', countColor: 'text-amber-600' },
          { status: 'Approved', count: approvedCount, holidays: approvedHolidays, icon: Check, border: 'border-green-200', bg: 'bg-green-50', iconColor: 'text-green-600', countColor: 'text-green-600' },
          { status: 'Denied', count: deniedCount, holidays: deniedHolidays, icon: XCircle, border: 'border-red-200', bg: 'bg-red-50', iconColor: 'text-red-600', countColor: 'text-red-600' },
          { status: 'Cancelled', count: cancelledCount, holidays: cancelledHolidays, icon: X, border: 'border-neutral-200', bg: 'bg-neutral-50', iconColor: 'text-neutral-600', countColor: 'text-neutral-600' },
        ].map(({ status, count, holidays, icon: Icon, border, bg, iconColor, countColor }) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusPopup(status)}
            className={`flex items-center gap-2 p-4 rounded-lg border-2 text-left transition-colors ${border} ${bg} hover:opacity-90`}
          >
            <Icon className={`h-5 w-5 ${iconColor}`} />
            <div>
              <div className="font-semibold text-neutral-800">{status}</div>
              <div className={`text-2xl font-bold ${countColor}`}>{count}</div>
            </div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading holidays...</div>
        </div>
      ) : (
        <>
          {/* Calendar */}
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-neutral-800">Calendar</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 rounded-lg hover:bg-neutral-100"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="min-w-[140px] text-center font-medium">
                  {format(currentMonth, 'MMMM yyyy')}
                </span>
                <button
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 rounded-lg hover:bg-neutral-100"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="p-2 text-center text-xs font-medium text-neutral-500">
                  {day}
                </div>
              ))}
              {days.map((date, index) => {
                const isCurrentMonth = date && isSameMonth(date, currentMonth);
                const isToday = date && isSameDay(date, new Date());
                const holidays = getFilteredHolidaysForDate(date);
                const blocked = date && isDayBlocked(date);
                return (
                  <div
                    key={index}
                    className={`min-h-[80px] p-1 border rounded text-sm ${
                      !date ? 'bg-neutral-50 border-transparent' :
                      blocked ? 'bg-red-50 border-red-200' :
                      isToday ? 'bg-blue-50 border-blue-300' : 'bg-white border-neutral-200'
                    }`}
                  >
                    {date && (
                      <>
                        <div className={`font-medium ${!isCurrentMonth ? 'text-neutral-300' : blocked ? 'text-red-600' : isToday ? 'text-blue-600' : 'text-neutral-900'}`}>
                          {date.getDate()}
                        </div>
                        {blocked ? (
                          <div className="mt-1 text-xs text-red-600">Blocked</div>
                        ) : holidays.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {holidays.slice(0, 2).map(hr => (
                              <div key={hr.id} className={`text-xs px-1 py-0.5 rounded truncate ${getStatusColor(hr.status)}`}>
                                {hr.status}
                              </div>
                            ))}
                            {holidays.length > 2 && <div className="text-xs text-neutral-500">+{holidays.length - 2}</div>}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Add Holiday Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-neutral-800">Add Holiday</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({
                    employeeId: 0,
                    managerSupervisor: '',
                    venueDepartment: '',
                    startDate: '',
                    endDate: '',
                    totalHours: undefined,
                    reason: '',
                    status: 'Pending',
                  });
                  setHolidayTypes({});
                  setFormErrors({});
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddHoliday} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.startDate ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.startDate && <p className="mt-1 text-sm text-red-600">{formErrors.startDate}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.endDate ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.endDate && <p className="mt-1 text-sm text-red-600">{formErrors.endDate}</p>}
              </div>
              {formData.startDate && formData.endDate && generateDateRange(formData.startDate, formData.endDate).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Holiday Type *</label>
                  <p className="text-xs text-neutral-500 mb-2">Select holiday type for each day:</p>
                  {formErrors.holidayTypes && <p className="mb-2 text-sm text-red-600">{formErrors.holidayTypes}</p>}
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {generateDateRange(formData.startDate, formData.endDate).map(date => (
                      <div key={date} className="flex items-center gap-3">
                        <span className="text-sm text-neutral-700 min-w-[140px]">
                          {formatCompanyDateLocale(date + 'T00:00:00', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <select
                          value={holidayTypes[date] || ''}
                          onChange={e => {
                            setHolidayTypes({ ...holidayTypes, [date]: e.target.value });
                            if (formErrors.holidayTypes) setFormErrors({ ...formErrors, holidayTypes: '' });
                          }}
                          className={`flex-1 px-3 py-2 border rounded-md text-sm ${
                            (!holidayTypes[date] || !holidayTypes[date].trim()) && formErrors.holidayTypes
                              ? 'border-red-500' : 'border-neutral-300'
                          }`}
                        >
                          <option value="">Select type...</option>
                          {employeeLeaveTypes.length > 0 ? (
                            employeeLeaveTypes.map(lt => (
                              <option key={lt.leaveTypeId} value={lt.name}>
                                {lt.name} ({lt.takenDays} approved, {lt.pendingDays} pending, {lt.remainingDays} available)
                              </option>
                            ))
                          ) : (
                            <>
                              <option value="Paid Holiday">Paid Holiday</option>
                              <option value="Day Off">Day Off</option>
                            </>
                          )}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Total hours (optional)</label>
                <input
                  type="number"
                  min={0}
                  step={0.25}
                  value={formData.totalHours ?? ''}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      totalHours: e.target.value === '' ? undefined : parseFloat(e.target.value),
                    })
                  }
                  placeholder="e.g. 12 — deduct/pay these hours when approved"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                />
                <p className="mt-1 text-xs text-neutral-500">
                  For hourly leave (e.g. nurses). Leave blank to use day-based counting.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Manager/Supervisor *</label>
                <input
                  type="text"
                  value={formData.managerSupervisor}
                  onChange={e => setFormData({ ...formData, managerSupervisor: e.target.value })}
                  placeholder="Enter manager/supervisor name"
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.managerSupervisor ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.managerSupervisor && <p className="mt-1 text-sm text-red-600">{formErrors.managerSupervisor}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Venue/Department *</label>
                <input
                  type="text"
                  value={formData.venueDepartment}
                  onChange={e => setFormData({ ...formData, venueDepartment: e.target.value })}
                  placeholder="Enter venue/department"
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.venueDepartment ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.venueDepartment && <p className="mt-1 text-sm text-red-600">{formErrors.venueDepartment}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Reason *</label>
                <textarea
                  value={formData.reason || ''}
                  onChange={e => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Enter reason for holiday request"
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.reason ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.reason && <p className="mt-1 text-sm text-red-600">{formErrors.reason}</p>}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Details Popup */}
      {statusPopup && (() => {
        const holidays = statusPopup === 'Pending' ? pendingHolidays
          : statusPopup === 'Approved' ? approvedHolidays
          : statusPopup === 'Denied' ? deniedHolidays
          : cancelledHolidays;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-xl">
              <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4 shrink-0">
                <h2 className="text-lg font-semibold text-neutral-800">{statusPopup} Holiday Requests</h2>
                <button
                  onClick={() => setStatusPopup(null)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {holidays.length === 0 ? (
                  <p className="text-neutral-500 text-center py-8">No {statusPopup.toLowerCase()} holiday requests.</p>
                ) : (
                  <div className="divide-y divide-neutral-200 space-y-0">
                    {holidays
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map(holiday => {
                        const startDate = new Date(holiday.startDate);
                        const endDate = new Date(holiday.endDate);
                        const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                        return (
                          <div key={holiday.id} className="py-4 border-l-4 border-blue-500 pl-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-neutral-900">
                                    {formatCompanyDate(startDate)}
                                    {daysDiff > 1 && ` - ${formatCompanyDate(endDate)}`}
                                  </span>
                                </div>
                                <div className="mt-1 text-sm text-neutral-600 flex items-center gap-2 flex-wrap">
                                  {daysDiff} day{daysDiff > 1 ? 's' : ''}
                                  {holiday.totalHours != null && holiday.totalHours > 0 && (
                                    <span>· {holiday.totalHours}h</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => { setStatusPopup(null); setDetailsPopupHoliday(holiday); }}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline font-medium"
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                    Holiday type details
                                  </button>
                                </div>
                                {holiday.reason && <p className="mt-2 text-sm text-neutral-700 italic">{holiday.reason}</p>}
                                <div className="mt-2 text-sm text-neutral-600">
                                  <span className="font-medium">Manager/Supervisor:</span> {holiday.managerSupervisor}
                                </div>
                                <div className="text-sm text-neutral-600">
                                  <span className="font-medium">Venue/Department:</span> {holiday.venueDepartment}
                                </div>
                                {holiday.rejectionReason && (
                                  <div className="mt-2 text-sm text-red-600">
                                    <span className="font-medium">Rejection Reason:</span> {holiday.rejectionReason}
                                  </div>
                                )}
                              </div>
                              {holiday.status === 'Pending' && (
                                <button
                                  onClick={() => handleCancelHoliday(holiday)}
                                  className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                                >
                                  Cancel Holiday
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-neutral-200 flex justify-end shrink-0">
                <button
                  onClick={() => setStatusPopup(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Holiday Type Details Popup */}
      {detailsPopupHoliday && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-neutral-800">Holiday Type Details</h2>
              <button
                onClick={() => setDetailsPopupHoliday(null)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-neutral-600 mb-4">
                {formatCompanyDate(detailsPopupHoliday.startDate)}
                {detailsPopupHoliday.startDate !== detailsPopupHoliday.endDate && (
                  <> – {formatCompanyDate(detailsPopupHoliday.endDate)}</>
                )}
              </p>
              {detailsPopupHoliday.holidayTypes && detailsPopupHoliday.holidayTypes.length > 0 ? (
                <div className="space-y-2">
                  {[...detailsPopupHoliday.holidayTypes]
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(({ date, holidayType }) => (
                      <div key={date} className="flex justify-between items-center py-2 px-3 bg-neutral-50 rounded-md">
                        <span className="text-sm font-medium text-neutral-800">
                          {formatCompanyDateLocale(date + 'T00:00:00', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="text-sm font-medium px-2 py-1 rounded bg-blue-100 text-blue-800">
                          {holidayType}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500 italic">No holiday type details available for this request.</p>
              )}
              {employeeLeaveTypes.length > 0 && (
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <h3 className="text-sm font-semibold text-neutral-800 mb-2">Your Leave Balances</h3>
                  <div className="space-y-2">
                    {employeeLeaveTypes.map(lt => (
                      <div key={lt.leaveTypeId} className="flex justify-between items-center py-2 px-3 bg-neutral-50 rounded-md text-sm">
                        <span className="font-medium text-neutral-800">{lt.name}</span>
                        <span className="text-neutral-600 flex gap-3">
                          {lt.isAnnualLeave && lt.accruedDays != null && (
                            <span><span className="text-neutral-700 font-medium">{lt.accruedDays}</span> accrued</span>
                          )}
                          <span><span className="text-green-600 font-medium">{lt.takenDays}</span> {lt.isAnnualLeave ? 'used' : 'approved'}</span>
                          <span><span className="text-amber-600 font-medium">{lt.pendingDays}</span> pending</span>
                          <span><span className="text-blue-600 font-medium">{lt.remainingDays}</span> available</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => setDetailsPopupHoliday(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHolidays;
